import { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { CONTACTS_CRM_EXPORT_LIMIT } from "../../management/constants";
import ManualSelectionItem from "../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import type { SelectionDropdownItem } from "../../table-actions-panel/models/selection-dropdown-item/types";
import { fromUniqueIdToPair, isRevealable, toUniqueContactId } from "../helpers/common";
import exportSelectionDropdown from "../services/actions-panel/export-selection/exportSelectionDropdown";
import { tableRowsSelectionToggledAction } from "../store/action-creators";
import {
    selectCheckedUniqueContactIds,
    selectContactsCrmExportLimit,
    selectExportDestination,
} from "../store/selectors";
import type { ContactSearchEntry } from "../types/common";

type UseExportSelectionParams = {
    contacts: ContactSearchEntry[];
    totalCount: number;
    isLoading: boolean;
};

export function useExportSelection({ contacts, totalCount, isLoading }: UseExportSelectionParams) {
    const dispatch = useDispatch();
    const crmExportLimit = useSelector(selectContactsCrmExportLimit);
    const exportDestination = useSelector(selectExportDestination);
    const selectedContactsIds = useSelector(selectCheckedUniqueContactIds);

    const dropdownItems = useMemo(() => {
        const exportLimit =
            exportDestination === "excel" ? totalCount : Math.min(crmExportLimit, CONTACTS_CRM_EXPORT_LIMIT);
        return exportSelectionDropdown.getItems(totalCount, exportLimit);
    }, [totalCount, crmExportLimit, exportDestination]);

    const [activeSelectionItem, setActiveSelectionItem] = useState<SelectionDropdownItem>(
        exportSelectionDropdown.getInitiallySelectedItem(dropdownItems),
    );

    const topNOrPairs = useMemo(() => {
        return activeSelectionItem.id === ManualSelectionItem.ID
            ? selectedContactsIds.map(fromUniqueIdToPair)
            : activeSelectionItem.value;
    }, [activeSelectionItem, selectedContactsIds]);

    const manualUnrevealedCount = useMemo(() => {
        if (activeSelectionItem.id !== ManualSelectionItem.ID) {
            return 0;
        }
        const selectedContacts = contacts.filter((c) => selectedContactsIds.includes(toUniqueContactId(c)));
        return selectedContacts.filter((c) => !c.isRevealed && isRevealable(c)).length;
    }, [activeSelectionItem.id, contacts, selectedContactsIds]);

    useEffect(() => {
        if (activeSelectionItem.id === ManualSelectionItem.ID) {
            setActiveSelectionItem(new ManualSelectionItem(selectedContactsIds.length));
        }
    }, [selectedContactsIds.length, activeSelectionItem.id]);

    useEffect(() => {
        if (isLoading) {
            return;
        }
        setActiveSelectionItem(exportSelectionDropdown.getInitiallySelectedItem(dropdownItems));
    }, [isLoading, dropdownItems]);

    useEffect(() => {
        dispatch(tableRowsSelectionToggledAction(activeSelectionItem.id === ManualSelectionItem.ID));
    }, [activeSelectionItem]);

    return {
        activeSelectionItem,
        setActiveSelectionItem,
        dropdownItems,
        topNOrPairs,
        manualUnrevealedCount,
        selectedContactsIds,
        exportDestination,
    };
}
